// P12 (démo de Besançon, D11) — les commandes « Masquer » et « Afficher de nouveau ». Fichier à
// part (comme `partage/`) : components.tsx et obs.css bougent en parallèle dans d'autres lots.
import { useEffect, useRef, useState } from 'react'
import { EyeOff } from 'lucide-react'
import type { PinCtx } from './components'
import { estMasqueCtx } from './masques'
import type { Masque } from './prefs'
import './masques.css'

/** Durée de l'avis « Indicateur masqué · Annuler » (Marc, réunion du 13/10). */
const DUREE_AVIS_MASQUE_MS = 8000

/** Juste après « Masquer » (Marc, réunion du 13/10 : un indicateur masqué par erreur, et pas de
    chemin évident pour le rendre) : « Indicateur masqué · Annuler », discret, sans rien bloquer.
    Il s'efface seul au bout de `DUREE_AVIS_MASQUE_MS` — en suspens tant que le pointeur ou le
    focus est dessus —, au premier geste ailleurs dans la page, ou quand on annule. Rendu dans
    une zone `role="status"` tenue par la page (annoncé sans déplacer la lecture). `clavier` :
    le masquage a été fait au clavier — le bouton qui avait le focus vient de disparaître avec
    l'élément, le focus passe sur « Annuler » plutôt que de retomber en tête de page. */
export function AvisMasque({ masque, clavier, onAnnuler, onFermer }: {
  masque: Masque; clavier: boolean; onAnnuler: () => void; onFermer: () => void
}) {
  const boite = useRef<HTMLDivElement>(null)
  const annuler = useRef<HTMLButtonElement>(null)
  const [survol, setSurvol] = useState(false)
  const [focus, setFocus] = useState(clavier)
  useEffect(() => {
    if (clavier) annuler.current?.focus()
  }, [clavier])
  useEffect(() => {
    if (survol || focus) return
    const minuterie = setTimeout(onFermer, DUREE_AVIS_MASQUE_MS)
    return () => clearTimeout(minuterie)
  }, [survol, focus, onFermer])
  useEffect(() => {
    const ailleurs = (e: Event) => {
      if (!boite.current?.contains(e.target as Node | null)) onFermer()
    }
    document.addEventListener('pointerdown', ailleurs)
    return () => document.removeEventListener('pointerdown', ailleurs)
  }, [onFermer])
  const section = masque.k == null
  return (
    <div ref={boite} className="obs2-avis-masque"
         onMouseEnter={() => setSurvol(true)} onMouseLeave={() => setSurvol(false)}
         onFocus={() => setFocus(true)}
         onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false) }}>
      <EyeOff size={13} aria-hidden="true" />
      <span>{section ? 'Section masquée' : 'Indicateur masqué'}</span>
      <span aria-hidden="true">·</span>
      <button ref={annuler} type="button" className="obs2-avis-masque-annuler"
        aria-label={section ? 'Annuler : afficher de nouveau cette section'
                            : `Annuler : afficher de nouveau « ${masque.k} »`}
        onClick={onAnnuler}>
        Annuler
      </button>
    </div>
  )
}

// P12 (démo de Besançon, D11) — « Masquer » : le complément de l'épingle, à côté d'elle. Rien
// n'est effacé ; l'élément revient par « Afficher les éléments masqués » puis « Afficher de
// nouveau ». Cible de 24 px au moins (WCAG 2.5.8), comme le bouton de partage.
export function MasquerBtn({ ctx, k, c }: { ctx: PinCtx; k: string; c?: boolean }) {
  if (!ctx.api.basculerMasque || estMasqueCtx(ctx, k, c)) return null
  const basculer = ctx.api.basculerMasque
  return (
    <button type="button" className="obs2-masquer"
      aria-label={`Masquer « ${k} » pour cette commune`}
      title="Masquer pour cette commune (réversible)"
      onClick={e => { e.preventDefault(); e.stopPropagation(); basculer({ t: ctx.t, s: ctx.s, k, c: !!c }) }}>
      <EyeOff size={13} aria-hidden="true" />
    </button>
  )
}

/** Le bandeau d'un élément masqué rendu parce que l'utilisateur a demandé à voir les masqués :
    le dit en clair et porte « Afficher de nouveau ». `k` absent : la section entière. */
export function BandeauMasque({ ctx, k, c, quoi = 'Masqué' }:
                              { ctx: PinCtx; k?: string; c?: boolean; quoi?: string }) {
  const basculer = ctx.api.basculerMasque
  if (!basculer) return null
  return (
    <div className="obs2-masque-bandeau">
      <EyeOff size={12} aria-hidden="true" /> {quoi} pour cette commune
      <button type="button" className="obs2-masque-reafficher"
        aria-label={k ? `Afficher de nouveau « ${k} »` : 'Afficher de nouveau cette section'}
        onClick={e => {
          e.preventDefault(); e.stopPropagation()
          basculer(k === undefined ? { t: ctx.t, s: ctx.s } : { t: ctx.t, s: ctx.s, k, c: !!c })
        }}>
        Afficher de nouveau
      </button>
    </div>
  )
}
